import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, Plus, Pencil, Trash2, Building2, Star, ExternalLink,
  RefreshCw, MessageSquare, Loader2, Search, Check,
} from "lucide-react";

const TYPES = [
  { value: "head_office", label: "Head office" },
  { value: "branch",      label: "Branch" },
  { value: "showroom",    label: "Showroom" },
  { value: "warehouse",   label: "Warehouse" },
  { value: "other",       label: "Other" },
];

const blank = () => ({
  name: "", location_type: "branch",
  address_line1: "", address_line2: "",
  city: "", state: "", postcode: "", country: "Australia",
  phone: "", email: "", timezone: "",
  gmb_place_id: "", gmb_account_id: "", gmb_location_id: "", gmb_review_url: "",
});

export default function ClientLocationsPage() {
  const { clientId } = useParams();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [reviewsFor, setReviewsFor] = useState(null);   // {location_id}
  const [reviewsData, setReviewsData] = useState(null); // {reviews:[...], branch:{...}}
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/locations`);
      setLocations(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [clientId]);

  const startCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const startEdit = (l) => {
    setEditing(l);
    setForm({ ...blank(), ...Object.fromEntries(
      Object.entries(l).filter(([_, v]) => v != null)
    ) });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== "")
      );
      if (editing) {
        await api.patch(`/clients/${clientId}/locations/${editing.location_id}`, payload);
        toast.success("Location updated");
      } else {
        await api.post(`/clients/${clientId}/locations`, payload);
        toast.success("Location added");
      }
      setOpen(false); setEditing(null); setForm(blank()); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete location "${l.name}"?`)) return;
    try {
      await api.delete(`/clients/${clientId}/locations/${l.location_id}`);
      toast.success("Deleted"); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const syncOne = async (l) => {
    setSyncingId(l.location_id);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/locations/${l.location_id}/gmb/sync-reviews`
      );
      toast.success(
        `Synced ${data.fetched} reviews · ${data.average_rating?.toFixed?.(1) || "—"}★ avg`
      );
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncingId(null); }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/gmb/sync-all`);
      toast.success(`${data.synced || 0} branch(es) synced`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync-all failed");
    } finally { setSyncingAll(false); }
  };

  const openReviews = async (l) => {
    setReviewsFor(l); setReviewsLoading(true); setReviewsData(null);
    try {
      const { data } = await api.get(
        `/clients/${clientId}/locations/${l.location_id}/gmb/reviews?limit=100`
      );
      setReviewsData(data);
    } catch (e) {
      toast.error("Failed to load reviews");
    } finally { setReviewsLoading(false); }
  };

  const anyLinked = locations.some((l) => l.gmb_account_id && l.gmb_location_id);

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div className="flex items-end justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#002FA7] text-white">
            <MapPin className="h-6 w-6" />
          </div>
          <div>
            <div className="label-overline">Branches</div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight">Locations</h1>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Add every physical branch / office. Each can carry its own Google
              Business Profile IDs so reviews and analytics flow per branch.
            </p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <div className="flex items-center gap-2">
              {anyLinked && (
                <Button onClick={syncAll} disabled={syncingAll}
                  variant="outline"
                  className="rounded-sm border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  data-testid="sync-all-gmb">
                  {syncingAll
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <RefreshCw className="h-4 w-4 mr-1" />}
                  Sync all reviews
                </Button>
              )}
              <Button onClick={startCreate}
                className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="add-location">
                <Plus className="h-4 w-4 mr-1" /> Add location
              </Button>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm" data-testid="location-dialog">
            <DialogHeader>
              <DialogTitle className="font-display font-bold text-2xl tracking-tight">
                {editing ? "Edit location" : "New location"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <F label="Name" required value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })} testid="loc-name" />
                <F label="Type" type="select" options={TYPES} value={form.location_type}
                  onChange={(v) => setForm({ ...form, location_type: v })} testid="loc-type" />
              </div>
              <F label="Address line 1" value={form.address_line1}
                onChange={(v) => setForm({ ...form, address_line1: v })} testid="loc-addr1" />
              <F label="Address line 2" value={form.address_line2}
                onChange={(v) => setForm({ ...form, address_line2: v })} testid="loc-addr2" />
              <div className="grid grid-cols-4 gap-3">
                <F label="City" value={form.city}
                  onChange={(v) => setForm({ ...form, city: v })} testid="loc-city" />
                <F label="State" value={form.state}
                  onChange={(v) => setForm({ ...form, state: v })} testid="loc-state" />
                <F label="Postcode" value={form.postcode}
                  onChange={(v) => setForm({ ...form, postcode: v })} testid="loc-postcode" />
                <F label="Country" value={form.country}
                  onChange={(v) => setForm({ ...form, country: v })} testid="loc-country" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Phone" value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })} testid="loc-phone" />
                <F label="Email" type="email" value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })} testid="loc-email" />
              </div>
              <div className="border-t border-zinc-200 pt-3 mt-2">
                <div className="label-overline mb-2 flex items-center gap-1">
                  <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                  Google Business Profile (per-branch)
                </div>
                <p className="text-[11px] text-zinc-500 mb-2">
                  Each branch has its own GBP listing. Click{" "}
                  <b>Pick from my Google account</b> to auto-fill the
                  IDs below from every business you can already manage
                  in the connected Gmail(s). Otherwise the raw IDs live
                  in your GBP dashboard → Settings → Advanced settings.
                </p>
                <GbpPicker
                  onPick={(loc) => setForm({
                    ...form,
                    gmb_place_id: loc.gmb_place_id || form.gmb_place_id,
                    gmb_account_id: loc.gmb_account_id || form.gmb_account_id,
                    gmb_location_id: loc.gmb_location_id || form.gmb_location_id,
                    gmb_review_url: loc.review_url || form.gmb_review_url,
                    website: (!form.website && loc.website) ? loc.website : form.website,
                    phone: (!form.phone && loc.phone) ? loc.phone : form.phone,
                  })}
                  currentPlaceId={form.gmb_place_id}
                  currentLocationId={form.gmb_location_id}
                />
                <F label="GMB Place ID" value={form.gmb_place_id}
                  onChange={(v) => setForm({ ...form, gmb_place_id: v })}
                  placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
                  testid="loc-place-id" mono />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <F label="GMB Account ID" value={form.gmb_account_id}
                    onChange={(v) => setForm({ ...form, gmb_account_id: v })}
                    placeholder="accounts/123…" testid="loc-gmb-account" mono />
                  <F label="GMB Location ID" value={form.gmb_location_id}
                    onChange={(v) => setForm({ ...form, gmb_location_id: v })}
                    placeholder="locations/456…" testid="loc-gmb-location" mono />
                </div>
                <F label="Public review URL" value={form.gmb_review_url}
                  onChange={(v) => setForm({ ...form, gmb_review_url: v })}
                  placeholder="https://g.page/r/…" testid="loc-review-url" mono />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy || !form.name}
                  className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="loc-submit">
                  {busy ? "Saving…" : (editing ? "Save changes" : "Add location")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <CardContent className="p-0">
          {locations.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
              <div className="font-display font-bold text-lg">No locations yet</div>
              <p className="text-sm text-zinc-500 mt-1">Click <strong>Add location</strong> to create the first branch.</p>
            </div>
          ) : locations.map((l) => {
            const linked = !!(l.gmb_account_id && l.gmb_location_id);
            const rating = Number(l.gmb_average_rating || 0);
            const count = Number(l.gmb_review_count || 0);
            return (
            <div key={l.location_id}
              className="grid grid-cols-[1fr_auto] gap-4 px-5 py-4 border-b border-zinc-100 last:border-b-0"
              data-testid={`location-row-${l.location_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Link to={`/clients/${clientId}/locations/${l.location_id}`}
                    className="font-display font-bold text-base truncate text-[#002FA7] hover:underline"
                    data-testid={`location-open-${l.location_id}`}>
                    {l.name}
                  </Link>
                  <Badge variant="outline"
                    className={`rounded-sm text-[9px] font-mono ${
                      l.location_type === "head_office"
                        ? "border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5"
                        : "border-zinc-300 text-zinc-600 bg-zinc-50"
                    }`}>
                    {(TYPES.find((t) => t.value === l.location_type) || TYPES[1]).label}
                  </Badge>
                  {linked ? (
                    <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 text-[9px]">
                      <Star className="h-3 w-3 mr-1 fill-emerald-700 text-emerald-700" /> GBP linked
                    </Badge>
                  ) : l.gmb_place_id && (
                    <Badge className="rounded-sm bg-amber-100 text-amber-800 border-amber-200 text-[9px]">
                      <Star className="h-3 w-3 mr-1 text-amber-700" /> Place ID only
                    </Badge>
                  )}
                  {count > 0 && (
                    <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 text-[9px]"
                      data-testid={`review-headline-${l.location_id}`}>
                      <Star className="h-3 w-3 mr-1 fill-amber-500 text-amber-500" />
                      {rating ? rating.toFixed(1) : "—"} · {count.toLocaleString()} reviews
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] font-mono text-zinc-500">
                  {[l.address_line1, l.address_line2, l.city, l.state, l.postcode, l.country]
                    .filter(Boolean).join(", ") || "—"}
                </div>
                <div className="text-[11px] font-mono text-zinc-400 mt-0.5 flex gap-3 flex-wrap">
                  {l.phone && <span>{l.phone}</span>}
                  {l.email && <span>{l.email}</span>}
                  {l.gmb_place_id && <span>place: {l.gmb_place_id.slice(0, 22)}…</span>}
                  {l.gmb_last_synced_at && (
                    <span>synced: {String(l.gmb_last_synced_at).slice(0, 16).replace("T", " ")}</span>
                  )}
                  {l.gmb_review_url && (
                    <a href={l.gmb_review_url} target="_blank" rel="noreferrer"
                      className="text-[#002FA7] hover:underline inline-flex items-center gap-0.5">
                      reviews <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {linked && (
                  <>
                    <Button size="sm" variant="outline"
                      className="rounded-sm h-8 text-[11px] px-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                      onClick={() => syncOne(l)} disabled={syncingId === l.location_id}
                      data-testid={`sync-gmb-${l.location_id}`}>
                      {syncingId === l.location_id
                        ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        : <RefreshCw className="h-3 w-3 mr-1" />}
                      Sync
                    </Button>
                    {count > 0 && (
                      <Button size="sm" variant="outline"
                        className="rounded-sm h-8 text-[11px] px-2"
                        onClick={() => openReviews(l)}
                        data-testid={`view-reviews-${l.location_id}`}>
                        <MessageSquare className="h-3 w-3 mr-1" /> Reviews
                      </Button>
                    )}
                  </>
                )}
                <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
                  onClick={() => startEdit(l)}
                  data-testid={`edit-location-${l.location_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-red-600"
                  onClick={() => remove(l)}
                  data-testid={`delete-location-${l.location_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );})}
        </CardContent>
      </Card>

      {/* Reviews drawer */}
      <Dialog open={!!reviewsFor} onOpenChange={(o) => { if (!o) { setReviewsFor(null); setReviewsData(null); } }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-hidden flex flex-col" data-testid="reviews-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-xl tracking-tight">
              {reviewsFor?.name} · Google reviews
            </DialogTitle>
            <div className="text-[11px] font-mono text-zinc-500 mt-1">
              {reviewsFor?.gmb_review_count || 0} reviews · {Number(reviewsFor?.gmb_average_rating || 0).toFixed(1)}★ avg
              {reviewsFor?.gmb_last_synced_at && (
                <span> · last synced {String(reviewsFor.gmb_last_synced_at).slice(0, 16).replace("T", " ")}</span>
              )}
            </div>
          </DialogHeader>
          {reviewsLoading ? (
            <div className="py-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading reviews…
            </div>
          ) : !reviewsData?.reviews?.length ? (
            <div className="py-10 text-center text-sm text-zinc-400">
              No reviews stored yet. Click <strong>Sync</strong> on the branch.
            </div>
          ) : (
            <div className="overflow-y-auto pr-1 space-y-3">
              {reviewsData.reviews.map((r) => (
                <div key={r.review_id} className="rounded-sm border border-zinc-200 p-3"
                  data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      {r.reviewer_photo && (
                        <img src={r.reviewer_photo} alt="" className="h-6 w-6 rounded-full object-cover border border-zinc-200" />
                      )}
                      <span className="font-display font-bold text-sm truncate">{r.reviewer_name || "Anonymous"}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} className={`h-3.5 w-3.5 ${n <= r.star_rating ? "fill-amber-500 text-amber-500" : "text-zinc-300"}`} />
                      ))}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-400 mb-1">
                    {String(r.create_time || "").slice(0, 10)}
                  </div>
                  {r.comment && (
                    <p className="text-sm text-zinc-700 whitespace-pre-line">{r.comment}</p>
                  )}
                  {r.reply_comment && (
                    <div className="mt-2 pl-3 border-l-2 border-emerald-300 bg-emerald-50/50 p-2 rounded-sm">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-800 mb-0.5">
                        Owner reply · {String(r.reply_update_time || "").slice(0, 10)}
                      </div>
                      <p className="text-xs text-emerald-900 whitespace-pre-line">{r.reply_comment}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, value, onChange, type = "text", options, required, testid, placeholder, mono }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-overline">{label}{required && " *"}</Label>
      {type === "select" ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="h-10 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
          data-testid={`${testid}-input`}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <Input type={type} required={required} value={value || ""} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`rounded-sm h-10 ${mono ? "font-mono text-xs" : ""}`}
          data-testid={`${testid}-input`} />
      )}
    </div>
  );
}

/* ── GbpPicker ─────────────────────────────────────────────────────
 *  Fetches the flat list of every Google Business Profile location
 *  visible to any connected Gmail (`/api/integrations/google/gmb/
 *  available-locations`), lets the admin pick one, and calls
 *  `onPick(loc)` to auto-fill the form fields. Includes a search
 *  filter + "already linked" hint so admins don't accidentally
 *  double-attach the same GBP to two branches.
 * ---------------------------------------------------------------- */
function GbpPicker({ onPick, currentPlaceId, currentLocationId }) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          "/integrations/google/gmb/available-locations");
        setLocations(data.locations || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail
          || "Failed to load Google Business Profiles. Make sure a "
          + "Google account is connected in Integrations → Google.");
      } finally { setLoading(false); }
    })();
  }, [open]);

  const filtered = q.trim()
    ? locations.filter((l) => {
        const needle = q.trim().toLowerCase();
        return (l.title || "").toLowerCase().includes(needle)
          || (l.address || "").toLowerCase().includes(needle)
          || (l.google_email || "").toLowerCase().includes(needle);
      })
    : locations;

  return (
    <div className="mb-3">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button type="button" size="sm" variant="outline"
            className="rounded-sm h-8 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5"
            data-testid="gbp-picker-open">
            <Search className="h-3.5 w-3.5 mr-1" />
            Pick from my Google account
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-3xl rounded-sm max-h-[85vh] flex flex-col"
          data-testid="gbp-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-xl tracking-tight">
              Pick a Google Business Profile
            </DialogTitle>
            <p className="text-[11px] text-zinc-500 mt-1">
              Every business your connected Google accounts manage — click
              one to auto-fill the Place ID, Account ID, Location ID, and
              public review URL below.
            </p>
          </DialogHeader>
          <div className="relative mt-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, address, or Gmail…"
              className="pl-7 h-9 rounded-sm"
              data-testid="gbp-picker-search" />
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading GBP locations…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-zinc-400 space-y-1">
              <div>No Google Business Profiles found.</div>
              <div className="text-[11px]">
                Connect a Google account in <b>Integrations → Google</b> and
                run a <b>GMB resync</b>, then reopen this picker.
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto pr-1 space-y-2 flex-1"
              data-testid="gbp-picker-list">
              {filtered.map((l) => {
                const isCurrent = (currentPlaceId && l.gmb_place_id === currentPlaceId)
                  || (currentLocationId && l.gmb_location_id === currentLocationId);
                return (
                  <button type="button" key={`${l.gmb_account_id}/${l.gmb_location_id}`}
                    onClick={() => { onPick(l); setOpen(false); }}
                    className={`w-full text-left border rounded-sm p-3 transition
                      ${isCurrent ? "border-[#002FA7] bg-[#002FA7]/5"
                        : "border-zinc-200 hover:border-[#002FA7] hover:bg-zinc-50"}`}
                    data-testid={`gbp-pick-${l.gmb_location_id?.split("/").pop()}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">
                          {l.title}
                          {isCurrent && (
                            <Badge variant="outline"
                              className="ml-2 rounded-sm text-[9px] bg-[#002FA7] text-white border-[#002FA7]">
                              <Check className="h-2.5 w-2.5 mr-0.5" /> current
                            </Badge>
                          )}
                          {l.already_linked && !isCurrent && (
                            <Badge variant="outline"
                              className="ml-2 rounded-sm text-[9px] bg-amber-50 text-amber-800 border-amber-200">
                              already linked{l.linked_to?.name ? ` to ${l.linked_to.name}` : ""}
                            </Badge>
                          )}
                        </div>
                        {l.address && (
                          <div className="text-[11px] text-zinc-500 truncate mt-0.5">
                            {l.address}
                          </div>
                        )}
                        <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                          {l.google_email} · {l.gmb_location_id}
                        </div>
                      </div>
                      {l.review_url && (
                        <a href={l.review_url} target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] text-[#002FA7] hover:underline inline-flex items-center gap-0.5 shrink-0">
                          reviews <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

